import axios from 'axios';
import { SettingsQueries } from '../graphql/settings.queries';
import { IIntegrationStatus } from '@/settings/integrations/types/integration-list-item';
import { IIntegrationModalDetails } from '@/settings/integrations/types/integration-modal';
import { ILimeField } from '@/settings/integrations/types/integration-lime-field';
import { apolloClient } from '@/apollo';
import { EFetchPolicy } from '@/api/types/enums/fetch-policy';

export class IntegrationService {
  private static readonly integrationName = 'integration-name';
  private static readonly saveIntegrationUrl = '/save/integration';

  static loadStatuses = async (): Promise<IIntegrationStatus[]> => {
    const {
      data: { integrations },
    } = await apolloClient.query({
      query: SettingsQueries.integrationsStatus,
      fetchPolicy: EFetchPolicy.NetworkOnly,
    });
    return integrations || [];
  };

  static loadDetails = async (name: string): Promise<IIntegrationModalDetails> => {
    const {
      data: { integration },
    } = await apolloClient.query({
      query: SettingsQueries.integrationDetails,
      variables: { name },
      fetchPolicy: EFetchPolicy.NetworkOnly,
    });
    return (
      {
        ...integration,
        haveToken: integration.token ? '1' : '0',
      } || null
    );
  };

  static setDetails = async (newDetails: IIntegrationModalDetails): Promise<number> => {
    const data = new FormData();
    data.append('integration-identifier', newDetails.identifier);
    data.append('integration-token', newDetails.token);
    data.append('integration-meta_data_1', newDetails.metaData1);
    data.append('integration-external_url', newDetails.externalUrl);
    data.append('integration-meta_data_2', newDetails.metaData2);
    data.append('integration-meta_data_3', newDetails.metaData3);
    data.append('action', 'save');
    data.append('integration-status', newDetails.status);
    data.append('integration-user_id', newDetails.userId);
    data.append(IntegrationService.integrationName, newDetails.name);
    const {
      data: { integration },
    } = await axios.post(IntegrationService.saveIntegrationUrl, data);
    return integration;
  };

  static verifyPhone = async (newDetails: IIntegrationModalDetails): Promise<string> => {
    const body = new FormData();
    body.append('integration-meta_data_1', newDetails.metaData1);
    body.append('action', 'verify_phone');
    body.append('integration-user', newDetails.userId);
    body.append(IntegrationService.integrationName, newDetails.name);

    const { data } = await axios.post(IntegrationService.saveIntegrationUrl, body);
    if (data.status !== 2) {
      throw new Error(data.status);
    }
    return data.validation_code;
  };

  static disconnect = async (name: string): Promise<number> => {
    const data = new FormData();
    data.append('action', 'delete');
    data.append(IntegrationService.integrationName, name);
    const {
      data: { integration },
    } = await axios.post(IntegrationService.saveIntegrationUrl, data);
    return integration;
  };

  static loadLimeCrmFields = async (domain: string): Promise<ILimeField[]> => {
    const data = new FormData();
    data.append('domain', domain);
    data.append('action', 'limetype');
    data.append('table', 'document');
    const {
      data: { fields },
    } = await axios.post('/get/integration/limecrm/limetype', data);
    return fields as ILimeField[];
  };

  static generateSCIMData = async (): Promise<Partial<IIntegrationModalDetails>> => {
    const {
      data: { integration },
    } = await axios.post('/get/integration/scim/generate');
    return {
      externalUrl: integration.external_url,
      haveToken: integration.token,
    };
  };

  static generateSympaData = async (): Promise<Partial<IIntegrationModalDetails>> => {
    const {
      data: { integration },
    } = await axios.get('/get/integration/sympa/generate');
    return {
      haveToken: integration.token,
    };
  };

  static generateEmailDocImpData = async (): Promise<Partial<IIntegrationModalDetails>> => {
    const {
      data: { integration },
    } = await axios.post('/get/integration/email/generate');
    return {
      externalUrl: integration.general_url,
      identifier: integration.unique_url,
    };
  };
}
